import { FC, useContext } from "react";

import { Grid, useTheme } from "@mui/material";
import {
  StripeRecurringInterval,
  formatCurrencyAmount,
  getDollarValue,
  formatDateToMedDate,
  getDatePlusNMonths,
  getDatePlusNYears,
  FormType,
} from "@simplyk/common";

import { FrontendFormContext } from "../../contexts/FrontendFormContext";
import { FrontendTicketingContext, MembershipRenewalBuyerInfo } from "../../contexts/FrontendTicketingContext";
import { useLocaleContext } from "../../contexts/LocaleContext";
import { useTranslate } from "../../hooks/useTranslate";

import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const PaymentProcessorTotalWithTip: FC = () => {
  const theme = useTheme();
  const { membershipRenewalBuyerInfo } = useContext(FrontendTicketingContext);
  const { displayedFormAmount, tip, stripeRecurrenceInterval, currency, formType } = useContext(FrontendFormContext);

  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();
  const amount = getDollarValue(displayedFormAmount + tip);

  const isFormV2 = theme.constants.isFormV2;

  const renewalDate = getRenewalDate(membershipRenewalBuyerInfo, stripeRecurrenceInterval, formType);
  const formattedRenewalDate = renewalDate ? formatDateToMedDate(renewalDate, isoLocale) : undefined;

  return (
    <Grid
      item
      xs={12}
      sx={{
        borderTop: `1px solid ${isFormV2 ? theme.palette.border.form.moderate : theme.palette.neutral[90]}`,
        backgroundColor: isFormV2 ? theme.palette.surface.form.intense : theme.palette.grey[100],
        padding: theme.spacing(2),
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={4}>
          <DashboardTypography
            variant="body1"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: isFormV2 ? theme.palette.text.form.intense : undefined,
            }}
          >
            {t("donationForm", "total")}
          </DashboardTypography>
        </Grid>
        <Grid item xs={8} data-test="payment-processor-total-with-tip-amount">
          <DashboardTypography
            variant="body1"
            align="right"
            component="div"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: isFormV2 ? theme.palette.text.form.intense : undefined,
            }}
          >
            {currency && formatCurrencyAmount({ value: amount, locale: isoLocale, currency })}
            {stripeRecurrenceInterval &&
              ` / ${
                stripeRecurrenceInterval === StripeRecurringInterval.year
                  ? t("dashboard", "common.year")
                  : t("dashboard", "common.month")
              }`}
          </DashboardTypography>
          {stripeRecurrenceInterval && formattedRenewalDate && (
            <Grid item xs={12}>
              <Grid container justifyContent="flex-end">
                <DashboardTypography
                  vibe="text-form-supershy"
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "right",
                  }}
                  data-test="payment-processor-total-with-tip-renew-date"
                >
                  {t("ticketing", "payment.total.recurring", { date: formattedRenewalDate })}
                </DashboardTypography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export const getRenewalDate = (
  membershipRenewalBuyerInfo: MembershipRenewalBuyerInfo | undefined,
  stripeRecurrenceInterval: StripeRecurringInterval | null | undefined,
  formType: FormType
): Date | null => {
  // the shouldProposeAutomaticRenewal only affects the yearly subscription
  if (
    !membershipRenewalBuyerInfo?.shouldProposeAutomaticRenewal &&
    stripeRecurrenceInterval === StripeRecurringInterval.year
  ) {
    return null;
  }

  if (membershipRenewalBuyerInfo?.expirationDate) {
    return new Date(membershipRenewalBuyerInfo.expirationDate);
  }

  if (formType === FormType.DonationForm && stripeRecurrenceInterval) {
    switch (stripeRecurrenceInterval) {
      case StripeRecurringInterval.month:
        return getDatePlusNMonths(new Date(), 1);
      case StripeRecurringInterval.year:
        return getDatePlusNYears(new Date(), 1);
      default:
        return null;
    }
  }

  return null;
};
